/**
 * Results admin tab + shared drawers.
 *
 * Extracted from AdminApp.jsx (Feb 2026 modularisation phase 2). Owns
 * five components:
 *   - RetestConfirmModal (also used by People/UsersTab)
 *   - SalesCertReportDrawer (also used by People/UsersTab)
 *   - Results, ResultDetail (rendered by /admin/results routes)
 *   - TranscriptViewer (rendered inside ResultDetail)
 *
 * The drawer is used from both this tab and the People tab, so it's
 * exported as a named symbol.
 */
import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend,
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";
import { Download, Eye, FileText, MessageSquare, Play, ChevronDown, ChevronUp, Loader2, X, Send, CheckCircle2, AlertTriangle, Sparkles, Mic, ArrowUpRight, ArrowDownRight, ShieldCheck } from "lucide-react";
import api, { formatApiError, API } from "../../api";
import { useAuth } from "../../contexts/AuthContext";
import { trackDownload, trackClick } from "../../hooks/useActivityLogger";
import { PageHeader, Loading, Err, HeatCell } from "./_shared";

// ---------------- Report-only cards (moved from AdminApp.jsx) ----------------
function PerformanceOverlayCard({ email }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    if (!email) return;
    api.get(`/performance/${encodeURIComponent(email)}`).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [email]);
  if (!rows || rows.length === 0) return null;
  return (
    <div className="card p-6" data-testid="performance-overlay">
      <div className="overline mb-1">SALES PERFORMANCE OVERLAY</div>
      <h3 className="font-heading font-bold text-xl mb-4">Business outcomes (revenue, pipeline, forecast)</h3>
      <div className="overflow-x-auto">
        <table className="data-table">
          <thead><tr><th>Period</th><th>Actual ₹Cr</th><th>Target ₹Cr</th><th>Achievement</th><th>Pipeline ₹Cr</th><th>Forecast accuracy</th></tr></thead>
          <tbody>
            {rows.map((row, i) => {
              const ach = row.revenue_target ? (row.revenue_actual / row.revenue_target * 100) : null;
              return (
                <tr key={i}>
                  <td className="font-semibold">{row.period}</td>
                  <td>{row.revenue_actual ?? "—"}</td>
                  <td>{row.revenue_target ?? "—"}</td>
                  <td>{ach !== null ? <span className={`badge ${ach >= 100 ? "badge-success" : ach >= 80 ? "badge-warning" : "badge-red"}`}>{ach.toFixed(0)}%</span> : "—"}</td>
                  <td>{row.pipeline ?? "—"}</td>
                  <td>{row.forecast_accuracy ?? "—"}%</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LongitudinalTrendCard({ email, competenciesMeta }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!email) return;
    api.get(`/longitudinal/${encodeURIComponent(email)}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [email]);
  if (!data || data.series.length < 2) return null;
  const chartData = data.series.map((p) => ({
    cycle: p.cycle_name?.split(" ").slice(0, 2).join(" ") || "—",
    overall: p.overall_score,
    ...Object.fromEntries(Object.entries(p.competency_scores || {})),
  }));
  return (
    <div className="card p-6" data-testid="longitudinal-trend">
      <div className="overline mb-1 !text-[#EE2737]">LONGITUDINAL · CAPABILITY MOVEMENT</div>
      <h3 className="font-heading font-bold text-xl mb-1">{data.participant_name}'s trajectory across {data.series.length} cycles</h3>
      <p className="text-[13px] text-[#525252] mb-4">Composite + 7-competency movement over time.</p>
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
          <XAxis dataKey="cycle" tick={{ fontSize: 11 }} />
          <YAxis domain={[0, 5]} tick={{ fontSize: 11 }} />
          <Tooltip />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Bar dataKey="overall" fill="#EE2737" name="Overall" />
          {(competenciesMeta || []).map((c, i) => (
            <Bar key={c.key} dataKey={c.key} fill={["#0A0A0A", "#525252", "#878787", "#D97706", "#10B981", "#3B82F6", "#8B5CF6"][i % 7]} name={c.name.split(" ").slice(0, 2).join(" ")} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function IntegrityBanner({ integrity, assignmentId }) {
  const { user } = useAuth();
  if (!integrity) return null;
  const verdict = integrity.verdict;
  const autoSubmitted = integrity.auto_submitted;
  const isCriticalIssue = autoSubmitted || verdict === "Failed";
  const isWarning = verdict === "Manual Review";
  const isInfo = verdict === "Acceptable";
  const tone = isCriticalIssue
    ? { bg: "#FEE2E2", border: "#DC2626", text: "#7F1D1D", icon: "AlertTriangle" }
    : isWarning
      ? { bg: "#FEF3C7", border: "#D97706", text: "#78350F", icon: "AlertTriangle" }
      : isInfo
        ? { bg: "#DBEAFE", border: "#2563EB", text: "#1E3A8A", icon: "ShieldCheck" }
        : { bg: "#D1FAE5", border: "#10B981", text: "#065F46", icon: "ShieldCheck" };
  const Icon = tone.icon === "AlertTriangle" ? AlertTriangle : ShieldCheck;
  const isAdminOrLead = user?.role === "admin" || user?.role === "client_lead";

  return (
    <div
      className="border-l-4 p-5 flex gap-4 items-start"
      style={{ background: tone.bg, borderColor: tone.border, color: tone.text }}
      data-testid="integrity-banner"
    >
      <Icon size={22} className="flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-3 flex-wrap mb-1">
          <div className="font-heading font-black text-lg leading-none">
            {autoSubmitted ? "Attempt auto-submitted · " : ""}Integrity: {verdict}
          </div>
          <span className="text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 border" style={{ borderColor: tone.border }}>
            Score {integrity.score}/100
          </span>
        </div>
        {autoSubmitted && (
          <div className="text-[13.5px] font-semibold mb-1.5">
            This attempt was automatically submitted because the participant exceeded the 3-warning proctoring threshold. Whatever was answered up to that point has still been scored — but the integrity verdict should be considered when interpreting the result.
          </div>
        )}
        {integrity.summary && (
          <div className="text-[13px] leading-relaxed">{integrity.summary}</div>
        )}
        <div className="flex flex-wrap gap-2 mt-3">
          {Object.entries(integrity.event_counts || {}).map(([k, v]) => (
            <span key={k} className="text-[11px] font-mono px-2 py-0.5 bg-white/60 border" style={{ borderColor: tone.border }}>
              {k.replace(/_/g, " ")}: <b>{v}</b>
            </span>
          ))}
        </div>
        {isAdminOrLead && (
          <Link
            to={`/admin/integrity/${assignmentId}`}
            className="inline-block text-[12px] font-bold uppercase tracking-wider mt-3 underline"
            style={{ color: tone.text }}
            data-testid="open-integrity-from-report"
          >
            Open full integrity evidence →
          </Link>
        )}
      </div>
    </div>
  );
}


export function RetestConfirmModal({ state, onCancel, onConfirm }) {
  const { user, phase, result } = state;
  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="retest-modal">
      <div className="bg-white w-full max-w-md shadow-2xl fade-in">
        <div className="px-6 py-4 border-b border-[#E5E7EB]">
          <div className="overline text-[#DC2626]">RETEST · WIPE ATTEMPT DATA</div>
          <div className="font-heading font-bold text-lg">{user.name}</div>
          <div className="text-[11px] text-[#878787]">{user.email}</div>
        </div>
        <div className="p-6 space-y-4">
          {phase === "confirm" && (
            <>
              <p className="text-sm text-[#525252] leading-relaxed">
                This wipes <strong className="text-[#0A0A0A]">{user.name}</strong>&apos;s MCQ, Sales Pitch, Role Play, certificate, ID verification and proctor events. Their account and programme invitation are kept — invitation resets to <em>invited</em>, so they see a fresh Sales Cert hub next time they log in.
              </p>
              <div className="p-3 bg-[#FEF3C7] border-l-4 border-[#D97706] text-xs text-[#0A0A0A]">
                <strong>Cannot be undone.</strong> Any certificate they earned will be voided and needs to be re-earned by retaking the flow.
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button onClick={onCancel} className="btn btn-secondary" data-testid="retest-cancel">Cancel</button>
                <button onClick={onConfirm} disabled={phase !== "confirm"} className="btn !bg-[#DC2626] !text-white hover:!bg-[#991B1B] disabled:!opacity-50 disabled:cursor-not-allowed" data-testid="retest-confirm">Retest this candidate</button>
              </div>
            </>
          )}
          {phase === "running" && (
            <div className="flex items-center gap-3 text-sm text-[#525252]">
              <Loader2 size={16} className="animate-spin text-[#EE2737]" />
              Wiping {user.name}&apos;s attempt data…
            </div>
          )}
          {phase === "done" && result && !result.error && (
            <div>
              <div className="p-3 bg-[#E6F4EC] border-l-4 border-[#008A3D]">
                <div className="font-bold text-[#008A3D] text-sm">Retest ready</div>
                <div className="text-xs text-[#525252] mt-1">
                  {user.name} can now sign in and start the Sales Cert flow again from step 1. Wiped: {Object.entries(result.wiped || {}).map(([k, v]) => `${k}=${v}`).join(" · ") || "(nothing was scored yet)"}.
                </div>
              </div>
              <div className="flex justify-end mt-4">
                <button onClick={onCancel} className="btn btn-primary" data-testid="retest-done">Done</button>
              </div>
            </div>
          )}
          {phase === "done" && result && result.error && (
            <div>
              <div className="p-3 bg-[#FEE2E2] border-l-4 border-[#DC2626] text-xs text-[#991B1B]">{result.error}</div>
              <div className="flex justify-end mt-4">
                <button onClick={onCancel} className="btn btn-secondary">Close</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function SalesCertReportDrawer({ user, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [voice, setVoice] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setErr("");
    Promise.all([
      api.get(`/admin/salescert/report/${user.id}`),
      api.get(`/admin/voice-checks/${user.id}`).catch(() => ({ data: null })),
    ]).then(([rep, vc]) => {
      if (cancelled) return;
      setData(rep.data);
      setVoice(vc.data);
      setLoading(false);
    }).catch((e) => {
      if (cancelled) return;
      setErr(formatApiError(e));
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [user.id]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const mcq = data?.mcq;
  const pitch = data?.sales_pitch;
  const rp = data?.roleplay;

  return (
    <div className="fixed inset-0 z-[100] flex" data-testid="salescert-report-drawer">
      <div className="flex-1 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="w-full max-w-[720px] bg-white h-full overflow-y-auto shadow-2xl fade-in">
        <div className="sticky top-0 bg-white border-b border-[#E5E7EB] px-6 py-4 flex items-center justify-between z-10">
          <div>
            <div className="overline text-[#EE2737]">SALES CERT REPORT</div>
            <div className="font-heading font-bold text-lg">{user.name}</div>
            <div className="text-xs text-[#878787]">{user.email} · {user.role_level || "L?"} · {user.region || "—"}</div>
          </div>
          <button onClick={onClose} className="text-[#878787] hover:text-[#0A0A0A] p-2" data-testid="drawer-close">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {loading && <div className="text-sm text-[#878787]">Loading report…</div>}
          {err && <div className="p-4 bg-[#FEE2E2] border-l-4 border-[#DC2626] text-sm text-[#991B1B]">{err}</div>}
          {!loading && !err && data && (
            <>
              {/* Aggregate scoreline */}
              <div className="card p-5 bg-[#0A0A0A] text-white">
                <div className="overline text-white/60 mb-2">AGGREGATE</div>
                <div className="flex items-baseline gap-3">
                  <div className="font-heading font-black text-4xl text-[#EE2737]">{data.marks_awarded || 0}</div>
                  <div className="text-lg text-white/60">/ {data.marks_max || 100}</div>
                  <div className="ml-auto text-sm">
                    {data.certified ? (
                      <span className="px-3 py-1 bg-[#008A3D] text-white font-bold text-xs uppercase tracking-widest">Certified</span>
                    ) : (
                      <span className="px-3 py-1 bg-[#525252] text-white font-bold text-xs uppercase tracking-widest">Not certified</span>
                    )}
                  </div>
                </div>
                <div className="mt-3 flex gap-4 text-xs text-white/70">
                  <span>MCQ: <strong className="text-white">{mcq?.marks_awarded ?? "—"}/{mcq?.marks_max ?? 50}</strong></span>
                  <span>Pitch: <strong className="text-white">{pitch?.marks_awarded ?? "—"}/{pitch?.marks_max ?? 25}</strong></span>
                  <span>Role Play: <strong className="text-white">{rp?.marks_awarded ?? "—"}/{rp?.marks_max ?? 25}</strong></span>
                </div>
              </div>

              {/* Voice checks — biometric audit trail */}
              <div data-testid="drawer-voice-checks">
                <div className="overline mb-2">VOICE BIOMETRIC · INTEGRITY AUDIT</div>
                {!voice ? (
                  <div className="text-xs text-[#8A8B9D]">Loading…</div>
                ) : !voice.enrolled ? (
                  <div className="card p-4 border-l-4 !border-[#D97706] bg-[#FEF3C7]">
                    <div className="text-sm font-bold text-[#92400E]">Not yet enrolled</div>
                    <div className="text-[11px] text-[#78350F] mt-1">Candidate has not completed the mic-check step. All voice-scored sections will be blocked until they do.</div>
                  </div>
                ) : (
                  <div className="card p-4 space-y-3">
                    <div className="flex items-center justify-between text-xs">
                      <div>
                        <div className="font-bold text-[#1A1A24]">Enrolled {new Date(voice.enrolled_at).toLocaleString()}</div>
                        {/* iter57 — show BOTH thresholds the backend actually
                            applies. Pitch + Role Play use the "speaking" bar
                            (0.25 since iter57); shorter checks use the strict
                            default. The per-check table below shows the exact
                            threshold used for each row. */}
                        <div className="text-[#8A8B9D] mt-0.5">
                          Threshold · speaking <strong>{(voice.thresholds?.speaking ?? voice.current_threshold).toFixed(2)}</strong>
                          {" "}· other <strong>{(voice.thresholds?.default ?? voice.current_threshold).toFixed(2)}</strong>
                          {" "}· {voice.enrolment_size_kb || "—"} KB sample
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <span className="px-2 py-0.5 bg-[#F0FDF4] text-[#065F46] text-[10px] font-bold uppercase tracking-widest border border-[#065F46]/20">{voice.passed_checks} pass</span>
                        {voice.failed_checks > 0 && (
                          <span className="px-2 py-0.5 bg-[#FEE2E2] text-[#991B1B] text-[10px] font-bold uppercase tracking-widest border border-[#991B1B]/20">{voice.failed_checks} fail</span>
                        )}
                      </div>
                    </div>
                    {voice.total_checks === 0 ? (
                      <div className="text-[11px] text-[#8A8B9D]">No voice checks logged yet — candidate has not attempted a voice-scored section.</div>
                    ) : (
                      <div className="max-h-56 overflow-auto -mx-1">
                        <table className="w-full text-[11px]">
                          <thead className="text-[10px] uppercase text-[#8A8B9D] sticky top-0 bg-white">
                            <tr>
                              <th className="text-left px-1 py-1">When</th>
                              <th className="text-left px-1 py-1">Section</th>
                              <th className="text-right px-1 py-1">Similarity</th>
                              <th className="text-right px-1 py-1">Threshold</th>
                              <th className="text-right px-1 py-1">Result</th>
                            </tr>
                          </thead>
                          <tbody>
                            {voice.checks.map((c) => {
                              // iter57 — borderline / margin now computed
                              // against the ACTUAL per-check threshold
                              // (c.threshold) not the single aggregate
                              // header value. Fallback to the speaking bar
                              // if the stored field is missing on a legacy row.
                              const effThreshold = typeof c.threshold === "number"
                                ? c.threshold
                                : (voice.thresholds?.speaking ?? voice.current_threshold);
                              const margin = (c.similarity ?? 0) - effThreshold;
                              const borderline = c.passed && margin >= 0 && margin < 0.05;
                              return (
                                <tr key={c.id} className="border-t border-[#F0F1F5]">
                                  <td className="px-1 py-1.5 tabular-nums text-[#535465]">{new Date(c.checked_at).toLocaleTimeString()}</td>
                                  <td className="px-1 py-1.5 text-[#535465]">{(c.kind || "").replace("salescert_", "").replace("_", " ")}</td>
                                  <td className={`px-1 py-1.5 text-right tabular-nums font-mono font-bold ${c.passed ? (borderline ? "text-[#D97706]" : "text-[#065F46]") : "text-[#991B1B]"}`}>{c.similarity?.toFixed(3)}</td>
                                  <td className="px-1 py-1.5 text-right tabular-nums font-mono text-[#8A8B9D]">{effThreshold?.toFixed?.(2) ?? "—"}</td>
                                  <td className="px-1 py-1.5 text-right">
                                    {c.passed ? (
                                      borderline ? <span className="text-[10px] font-bold text-[#D97706]">BORDERLINE</span> : <span className="text-[10px] font-bold text-[#065F46]">PASS</span>
                                    ) : (
                                      <span className="text-[10px] font-bold text-[#991B1B]">BLOCKED</span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* MCQ */}
              <div>
                <div className="overline mb-2">SECTION 1 · TIMED MCQ</div>
                {mcq ? (
                  <div className="card p-4 space-y-4" data-testid="drawer-mcq">
                    {/* Best-of-N headline */}
                    <div className="flex items-baseline gap-3 flex-wrap">
                      <div className="text-2xl font-black text-[#0A0A0A]">
                        {mcq.marks_awarded}<span className="text-sm text-[#878787] font-normal">/{mcq.marks_max}</span>
                      </div>
                      <div className="text-xs text-[#525252]">
                        {mcq.correct}/{mcq.total} correct
                      </div>
                      {(mcq.attempts_used || 0) > 1 && mcq.best_attempt_no && (
                        <span className="text-[10px] uppercase tracking-widest font-bold text-[#5F004B] bg-[#F5F3EE] px-2 py-1">
                          Best of {mcq.attempts_used} · Attempt {mcq.best_attempt_no} kept
                        </span>
                      )}
                    </div>

                    {/* Per-attempt strip — only shows when >1 attempt exists */}
                    {(mcq.attempts || []).length > 1 && (
                      <>
                        <div className="grid grid-cols-2 gap-2" data-testid="drawer-mcq-attempts">
                          {mcq.attempts.map((a) => (
                            <div
                              key={a.attempt_no}
                              className={`p-3 border ${a.is_best ? "border-[#EE2737] bg-[#FEF2F2]" : "border-[#E5E7EB] bg-[#FAFAFA]"}`}
                              data-testid={`drawer-mcq-attempt-${a.attempt_no}`}
                            >
                              <div className="flex items-center justify-between mb-1">
                                <div className="text-[10px] uppercase tracking-widest font-bold text-[#525252]">
                                  Attempt {a.attempt_no}
                                </div>
                                {a.is_best && (
                                  <span className="text-[9px] uppercase tracking-widest font-bold text-white bg-[#EE2737] px-1.5 py-0.5">
                                    Best · Kept
                                  </span>
                                )}
                              </div>
                              <div className="flex items-baseline gap-2">
                                <div className={`text-xl font-black ${a.is_best ? "text-[#EE2737]" : "text-[#525252]"}`}>
                                  {a.marks_awarded ?? "—"}
                                </div>
                                <div className="text-[11px] text-[#878787]">
                                  / {mcq.marks_max} · {a.correct ?? "—"}/{a.total ?? "—"} correct
                                </div>
                              </div>
                              <div className="text-[10px] text-[#878787] mt-1">
                                {a.submitted_at ? new Date(a.submitted_at).toLocaleString() : "Not submitted"}
                                {a.status && a.status !== "submitted" ? ` · ${a.status}` : ""}
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className="text-[10px] italic text-[#878787] leading-relaxed" data-testid="drawer-mcq-tiebreak-hint">
                          Tie-breaker: when both attempts score identically, the earlier attempt is kept (candidate&apos;s ceiling didn&apos;t improve on retake).
                        </div>
                      </>
                    )}

                    {/* Per-question breakdown — every attempt, side by side.
                        Each attempt's own `items` list preserves the questions
                        and choices as they were on that specific attempt so a
                        reviewer can eyeball where the retake gained or lost
                        ground vs the first pass. */}
                    {(mcq.attempts || []).length > 0 ? (
                      (mcq.attempts).map((a) => (
                        <div key={`items-${a.attempt_no}`} data-testid={`drawer-mcq-attempt-items-${a.attempt_no}`}>
                          <div className="flex items-center gap-2 mb-2">
                            <div className="text-[10px] uppercase tracking-widest font-bold text-[#878787]">
                              Per-question breakdown · Attempt {a.attempt_no}
                            </div>
                            {a.is_best && (
                              <span className="text-[9px] uppercase tracking-widest font-bold text-white bg-[#EE2737] px-1.5 py-0.5">
                                Best · Kept
                              </span>
                            )}
                            <span className="text-[10px] text-[#878787]">
                              {a.correct ?? "—"}/{a.total ?? "—"} correct · {a.marks_awarded ?? "—"}/{mcq.marks_max}
                            </span>
                          </div>
                          <div className={`space-y-2 ${a.is_best ? "" : "opacity-90"}`}>
                            {(a.items || []).slice(0, 10).map((it, i) => (
                              <div key={i} className={`text-xs p-2 border-l-2 ${it.is_correct ? "border-[#008A3D] bg-[#E6F4EC]" : "border-[#DC2626] bg-[#FEE2E2]"}`}>
                                <div className="font-semibold mb-1">Q{i + 1}. {(it.question || "").slice(0, 140)}{(it.question || "").length > 140 ? "…" : ""}</div>
                                <div>Chose <strong>{it.chosen ?? "—"}</strong> · Correct <strong>{it.correct_option}</strong></div>
                              </div>
                            ))}
                            {(!a.items || a.items.length === 0) && (
                              <div className="text-[11px] italic text-[#878787]">No per-question data — this attempt was forfeited without submission.</div>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      /* Legacy submissions predating the two-attempt refactor
                         only have a top-level `items` list. Fall back to that. */
                      <div>
                        <div className="text-[10px] uppercase tracking-widest font-bold text-[#878787] mb-2">
                          Per-question breakdown
                        </div>
                        <div className="space-y-2">
                          {(mcq.items || []).slice(0, 10).map((it, i) => (
                            <div key={i} className={`text-xs p-2 border-l-2 ${it.is_correct ? "border-[#008A3D] bg-[#E6F4EC]" : "border-[#DC2626] bg-[#FEE2E2]"}`}>
                              <div className="font-semibold mb-1">Q{i + 1}. {(it.question || "").slice(0, 140)}{(it.question || "").length > 140 ? "…" : ""}</div>
                              <div>Chose <strong>{it.chosen}</strong> · Correct <strong>{it.correct_option}</strong></div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : <div className="text-xs text-[#878787]">Not attempted.</div>}
              </div>

              {/* Sales Pitch */}
              <div>
                <div className="overline mb-2">SECTION 2 · SALES PITCH</div>
                {pitch ? (
                  <div className="card p-4 space-y-3">
                    <div className="text-sm flex items-baseline flex-wrap gap-2">
                      <span>Score <strong>{pitch.score_1_to_5}/5</strong> · {pitch.marks_awarded}/{pitch.marks_max} marks</span>
                      {(pitch.attempts_used || 0) > 1 && pitch.best_attempt_no && (
                        <span className="text-[10px] uppercase tracking-widest font-bold text-[#5F004B] bg-[#F5F3EE] px-2 py-1">
                          Best of {pitch.attempts_used} · Attempt {pitch.best_attempt_no} kept
                        </span>
                      )}
                      <Link
                        to="/admin/rubric"
                        target="_blank"
                        className="text-[10px] uppercase tracking-widest text-[#EE2737] underline hover:no-underline"
                        title="Open the rubric handbook — what 1/5 vs 5/5 mean"
                        data-testid="drawer-pitch-rubric-link"
                      >
                        What does {pitch.score_1_to_5}/5 mean? ↗
                      </Link>
                    </div>
                    {pitch.scenario_prompt && (pitch.attempts || []).length <= 1 && (
                      <details className="text-xs">
                        <summary className="cursor-pointer font-semibold text-[#525252]">Prompt they responded to</summary>
                        <div className="mt-2 p-3 bg-[#F5F3EE] whitespace-pre-wrap">{pitch.scenario_prompt}</div>
                      </details>
                    )}
                    {/* Per-attempt view — every attempt, labelled, with best flagged.
                        Both attempts' transcript + feedback stay visible for manual
                        moderation. Single-attempt fallback below preserves the
                        original layout for candidates who only ever recorded once. */}
                    {(pitch.attempts || []).length > 1 ? (
                      <div className="space-y-3" data-testid="drawer-pitch-attempts">
                        {pitch.attempts.map((a) => (
                          <div
                            key={a.attempt_no}
                            className={`p-3 border ${a.is_best ? "border-[#EE2737] bg-[#FEF2F2]" : "border-[#E5E7EB] bg-[#FAFAFA]"}`}
                            data-testid={`drawer-pitch-attempt-${a.attempt_no}`}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-baseline gap-2">
                                <div className="text-[10px] uppercase tracking-widest font-bold text-[#525252]">
                                  Attempt {a.attempt_no}
                                </div>
                                <div className={`text-lg font-black ${a.is_best ? "text-[#EE2737]" : "text-[#525252]"}`}>
                                  {a.score_1_to_5 ?? "—"}<span className="text-[11px] text-[#878787] font-normal">/5</span>
                                </div>
                                <div className="text-[11px] text-[#878787]">
                                  · {a.marks_awarded ?? "—"}/{a.marks_max ?? pitch.marks_max} marks
                                </div>
                              </div>
                              {a.is_best && (
                                <span className="text-[9px] uppercase tracking-widest font-bold text-white bg-[#EE2737] px-1.5 py-0.5">
                                  Best · Kept
                                </span>
                              )}
                            </div>
                            <div className="text-[10px] text-[#878787] mb-2">
                              {a.submitted_at ? new Date(a.submitted_at).toLocaleString() : "Not submitted"}
                            </div>
                            {a.scenario_prompt && (
                              <details className="text-xs mb-2" data-testid={`drawer-pitch-attempt-${a.attempt_no}-prompt`}>
                                <summary className="cursor-pointer font-semibold text-[#525252]">Prompt they responded to</summary>
                                <div className="mt-2 p-3 bg-[#F5F3EE] whitespace-pre-wrap">{a.scenario_prompt}</div>
                              </details>
                            )}
                            <div className="text-[10px] uppercase tracking-widest font-bold text-[#878787] mb-1">Transcript</div>
                            <div className="p-2 bg-white border border-[#E5E7EB] text-xs whitespace-pre-wrap max-h-40 overflow-y-auto mb-2">{a.transcript || "—"}</div>
                            <div className="text-[10px] uppercase tracking-widest font-bold text-[#878787] mb-1">Feedback</div>
                            <div className="text-xs text-[#525252] leading-relaxed">{a.feedback || "—"}</div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <>
                        <div>
                          <div className="text-[10px] uppercase tracking-widest font-bold text-[#878787] mb-1">Transcript</div>
                          <div className="p-3 bg-[#F5F3EE] text-xs whitespace-pre-wrap max-h-48 overflow-y-auto">{pitch.transcript || "—"}</div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-widest font-bold text-[#878787] mb-1">Feedback</div>
                          <div className="text-xs text-[#525252] leading-relaxed">{pitch.feedback || "—"}</div>
                        </div>
                      </>
                    )}
                  </div>
                ) : <div className="text-xs text-[#878787]">Not attempted.</div>}
              </div>

              {/* Role Play */}
              <div>
                <div className="overline mb-2">SECTION 3 · AI ROLE PLAY</div>
                {rp ? (
                  <div className="card p-4 space-y-3">
                    <div className="text-sm flex items-baseline flex-wrap gap-2">
                      <span>Score <strong>{rp.score_1_to_5}/5</strong> · {rp.marks_awarded}/{rp.marks_max} marks</span>
                      {(rp.attempts_used || 0) > 1 && rp.best_attempt_no && (
                        <span className="text-[10px] uppercase tracking-widest font-bold text-[#5F004B] bg-[#F5F3EE] px-2 py-1">
                          Best of {rp.attempts_used} · Attempt {rp.best_attempt_no} kept
                        </span>
                      )}
                      <Link
                        to="/admin/rubric"
                        target="_blank"
                        className="text-[10px] uppercase tracking-widest text-[#EE2737] underline hover:no-underline"
                        title="Open the rubric handbook — what 1/5 vs 5/5 mean"
                        data-testid="drawer-roleplay-rubric-link"
                      >
                        What does {rp.score_1_to_5}/5 mean? ↗
                      </Link>
                    </div>
                    {rp.scenario_summary && (rp.attempts || []).length <= 1 && (
                      <details className="text-xs">
                        <summary className="cursor-pointer font-semibold text-[#525252]">Scenario</summary>
                        <div className="mt-2 p-3 bg-[#F5F3EE] whitespace-pre-wrap">{rp.scenario_summary}</div>
                      </details>
                    )}
                    {/* Per-attempt view — every attempt with its own dialogue +
                        feedback so the moderator can compare both runs. */}
                    {(rp.attempts || []).length > 1 ? (
                      <div className="space-y-3" data-testid="drawer-roleplay-attempts">
                        {rp.attempts.map((a) => (
                          <div
                            key={a.attempt_no}
                            className={`p-3 border ${a.is_best ? "border-[#EE2737] bg-[#FEF2F2]" : "border-[#E5E7EB] bg-[#FAFAFA]"}`}
                            data-testid={`drawer-roleplay-attempt-${a.attempt_no}`}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-baseline gap-2">
                                <div className="text-[10px] uppercase tracking-widest font-bold text-[#525252]">
                                  Attempt {a.attempt_no}
                                </div>
                                <div className={`text-lg font-black ${a.is_best ? "text-[#EE2737]" : "text-[#525252]"}`}>
                                  {a.score_1_to_5 ?? "—"}<span className="text-[11px] text-[#878787] font-normal">/5</span>
                                </div>
                                <div className="text-[11px] text-[#878787]">
                                  · {a.marks_awarded ?? "—"}/{a.marks_max ?? rp.marks_max} marks
                                </div>
                              </div>
                              {a.is_best && (
                                <span className="text-[9px] uppercase tracking-widest font-bold text-white bg-[#EE2737] px-1.5 py-0.5">
                                  Best · Kept
                                </span>
                              )}
                            </div>
                            <div className="text-[10px] text-[#878787] mb-2">
                              {a.submitted_at ? new Date(a.submitted_at).toLocaleString() : "Not submitted"}
                            </div>
                            {a.scenario_prompt && (
                              <details className="text-xs mb-2" data-testid={`drawer-roleplay-attempt-${a.attempt_no}-prompt`}>
                                <summary className="cursor-pointer font-semibold text-[#525252]">Scenario</summary>
                                <div className="mt-2 p-3 bg-[#F5F3EE] whitespace-pre-wrap">{a.scenario_prompt}</div>
                              </details>
                            )}
                            <div className="text-[10px] uppercase tracking-widest font-bold text-[#878787] mb-1">Dialogue</div>
                            <div className="space-y-1.5 max-h-56 overflow-y-auto p-2 bg-white border border-[#E5E7EB] mb-2">
                              {(a.messages || []).length > 0 ? (a.messages).map((m, i) => (
                                <div key={i} className={`flex gap-2 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
                                  <div className={`text-[10px] uppercase font-bold px-1.5 py-0.5 h-fit ${m.role === "user" ? "bg-[#EE2737] text-white" : "bg-[#0A0A0A] text-white"}`}>{m.role === "user" ? "REP" : "AI"}</div>
                                  <div className={`text-xs p-2 flex-1 ${m.role === "user" ? "bg-[#F5F5F5]" : "bg-[#F5F3EE]"}`}>{m.content}</div>
                                </div>
                              )) : (
                                <div className="text-[11px] italic text-[#878787]">No dialogue captured for this attempt.</div>
                              )}
                            </div>
                            <div className="text-[10px] uppercase tracking-widest font-bold text-[#878787] mb-1">Feedback</div>
                            <div className="text-xs text-[#525252] leading-relaxed">{a.feedback || "—"}</div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <>
                        <div>
                          <div className="text-[10px] uppercase tracking-widest font-bold text-[#878787] mb-2">Dialogue</div>
                          <div className="space-y-2 max-h-64 overflow-y-auto">
                            {(rp.messages || []).map((m, i) => (
                              <div key={i} className={`flex gap-2 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
                                <div className={`text-[10px] uppercase font-bold px-1.5 py-0.5 h-fit ${m.role === "user" ? "bg-[#EE2737] text-white" : "bg-[#0A0A0A] text-white"}`}>{m.role === "user" ? "REP" : "AI"}</div>
                                <div className={`text-xs p-2 flex-1 ${m.role === "user" ? "bg-[#F5F5F5]" : "bg-[#F5F3EE]"}`}>{m.content}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-widest font-bold text-[#878787] mb-1">Feedback</div>
                          <div className="text-xs text-[#525252] leading-relaxed">{rp.feedback || "—"}</div>
                        </div>
                      </>
                    )}
                  </div>
                ) : <div className="text-xs text-[#878787]">Not attempted.</div>}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------- Results list (grouped by cycle) ----------------
export function Results() {
  const [assignments, setAssignments] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [activeCycle, setActiveCycle] = useState("all");
  const [downloading, setDownloading] = useState(null);
  const [salescertCandidates, setSalescertCandidates] = useState([]);
  const [drawerUser, setDrawerUser] = useState(null);
  useEffect(() => {
    api.get("/admin/assignments").then((r) => setAssignments(r.data));
    api.get("/admin/cycles").then((r) => setCycles(r.data || []));
    api.get("/admin/salescert/candidates").then((r) => setSalescertCandidates(r.data || [])).catch(() => {});
  }, []);

  const downloadCsv = async () => {
    setDownloading("csv");
    try {
      const res = await api.get("/admin/results/export.csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "VI_COMPASS_aggregate_results.csv"; document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);
    } finally { setDownloading(null); }
  };
  const downloadPdf = async (a) => {
    setDownloading(a.id);
    trackDownload("results_pdf", { participant: a.participant_name });
    try {
      const res = await api.get(`/results/${a.id}/export.pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url; link.download = `COMPASS_${(a.participant_name || "report").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(url);
    } finally { setDownloading(null); }
  };

  // Group assignments by cycle
  const cycleById = Object.fromEntries(cycles.map((c) => [c.id, c]));
  const grouped = assignments.reduce((acc, a) => {
    const cid = a.cycle_id || "_unassigned";
    if (!acc[cid]) acc[cid] = [];
    acc[cid].push(a);
    return acc;
  }, {});
  // Sort cycles by start_date desc (most recent first)
  const cycleOrder = [...cycles].sort((x, y) => (y.start_date || "").localeCompare(x.start_date || ""));
  const visibleCycleIds = activeCycle === "all"
    ? cycleOrder.map((c) => c.id).filter((id) => grouped[id])
    : [activeCycle].filter((id) => grouped[id]);

  return (
    <div className="space-y-8 fade-in">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <PageHeader eyebrow="ASSIGNMENT RESULTS" title="Per-participant results" subtitle="Click into any scored participant for the full competency report, or export the cohort to CSV." />
        <button onClick={downloadCsv} disabled={downloading === "csv"} className="btn btn-secondary" data-testid="export-results-csv">
          <Download size={14} /> {downloading === "csv" ? "Exporting…" : "Export cohort CSV"}
        </button>
      </div>

      {/* Sales Cert candidates — new pilot / go-live flow */}
      <div data-testid="salescert-results-section">
        <div className="flex items-baseline justify-between mb-3">
          <div>
            <div className="overline text-[#EE2737]">VI SALES CERTIFICATION 2026 · LIVE PILOT</div>
            <h2 className="font-heading font-bold text-xl mt-1">Sales Cert candidates ({salescertCandidates.length})</h2>
          </div>
          <span className="text-[10px] uppercase tracking-widest text-[#878787]">Click a row for the full report</span>
        </div>
        {salescertCandidates.length === 0 ? (
          <div className="card p-6 text-center text-sm text-[#878787]" data-testid="salescert-results-empty">
            No participant has started the Sales Cert flow yet.
          </div>
        ) : (
          <div className="card overflow-hidden">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th><th>Region</th><th>MCQ</th><th>Pitch</th><th>Role Play</th><th>Aggregate</th><th>Certified</th><th>Last activity</th>
                </tr>
              </thead>
              <tbody>
                {salescertCandidates.map((c) => (
                  <tr
                    key={c.user_id}
                    onClick={() => setDrawerUser({ id: c.user_id, name: c.name, email: c.email, role_level: c.role_level, region: c.region })}
                    className="cursor-pointer hover:bg-[#F5F3EE] transition-colors"
                    data-testid={`salescert-result-${c.email}`}
                  >
                    <td className="font-semibold">{c.name}<div className="text-[10px] text-[#878787] font-normal">{c.email}</div></td>
                    <td>{c.region || "—"}</td>
                    <td>{c.mcq_marks != null ? `${c.mcq_marks}/50` : <span className="text-[#878787]">—</span>}</td>
                    <td>{c.pitch_marks != null ? `${c.pitch_marks}/25` : <span className="text-[#878787]">—</span>}</td>
                    <td>{c.roleplay_marks != null ? `${c.roleplay_marks}/25` : <span className="text-[#878787]">—</span>}</td>
                    <td className="font-bold">{c.aggregate_marks}/100</td>
                    <td>{c.certified ? <span className="badge" style={{ background: "#008A3D", color: "white" }}>Yes</span> : <span className="text-[#878787]">—</span>}</td>
                    <td className="text-[11px] text-[#525252]">{c.last_activity ? new Date(c.last_activity).toLocaleString() : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {drawerUser && (
        <SalesCertReportDrawer user={drawerUser} onClose={() => setDrawerUser(null)} />
      )}

      {/* Cycle filter pills */}
      <div className="flex flex-wrap gap-2 items-center" data-testid="cycle-filter">
        <span className="overline mr-2">FILTER BY CYCLE</span>
        <button
          onClick={() => setActiveCycle("all")}
          className={`px-3 py-1.5 text-[12px] font-bold uppercase tracking-wider border ${activeCycle === "all" ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white text-[#525252] border-[#E5E7EB] hover:border-[#0A0A0A]"}`}
          data-testid="cycle-filter-all"
        >
          All cycles ({assignments.length})
        </button>
        {cycleOrder.map((c) => (
          <button
            key={c.id}
            onClick={() => setActiveCycle(c.id)}
            className={`px-3 py-1.5 text-[12px] font-bold uppercase tracking-wider border ${activeCycle === c.id ? "bg-[#EE2737] text-white border-[#EE2737]" : "bg-white text-[#525252] border-[#E5E7EB] hover:border-[#EE2737]"}`}
            data-testid={`cycle-filter-${c.id}`}
          >
            {c.name.replace(" Sales Capability Assessment", "")} ({(grouped[c.id] || []).length})
          </button>
        ))}
      </div>

      {visibleCycleIds.length === 0 && (
        <div className="card p-10 text-center text-[#878787] text-sm">No assignments in this cycle yet.</div>
      )}

      {visibleCycleIds.map((cid) => {
        const cyc = cycleById[cid];
        const rows = grouped[cid] || [];
        const scoredCount = rows.filter((a) => a.status === "scored").length;
        return (
          <div key={cid} className="space-y-3" data-testid={`cycle-group-${cid}`}>
            <div className="flex items-baseline justify-between flex-wrap gap-2 border-b border-[#E5E7EB] pb-2">
              <div>
                <div className="overline !text-[#EE2737]">{cyc?.status === "active" ? "ACTIVE CYCLE" : cyc?.status === "closed" ? "CLOSED" : "CYCLE"}</div>
                <h2 className="font-heading font-black text-2xl tracking-tight">{cyc?.name || "Unassigned"}</h2>
                <div className="text-[12px] text-[#878787] mt-0.5">
                  {cyc?.start_date} → {cyc?.end_date} · {rows.length} participants · {scoredCount} scored
                </div>
              </div>
            </div>
            <div className="card overflow-hidden">
              <table className="data-table">
                <thead>
                  <tr><th>Participant</th><th>Level</th><th>Status</th><th>Action</th></tr>
                </thead>
                <tbody>
                  {rows.map((a) => (
                    <tr key={a.id} data-testid={`assignment-row-${a.id}`}>
                      <td className="font-semibold">{a.participant_name}</td>
                      <td><span className="badge badge-red">{a.participant_role_level}</span></td>
                      <td>
                        <span className={`badge ${
                          a.status === "scored" ? "badge-success" :
                          a.status === "submitted" ? "badge-info" :
                          a.status === "in_progress" ? "badge-warning" : "badge-neutral"
                        }`}>{a.status}</span>
                      </td>
                      <td>
                        {a.status === "scored" || a.status === "submitted" ? (
                          <div className="flex items-center gap-3">
                            <Link to={`/admin/results/${a.id}`} className="text-[#EE2737] font-semibold text-sm hover:underline">View report →</Link>
                            <button onClick={() => downloadPdf(a)} disabled={downloading === a.id}
                              className="text-[#525252] hover:text-[#EE2737] text-xs font-semibold flex items-center gap-1"
                              data-testid={`export-pdf-${a.id}`}>
                              <Download size={12} /> {downloading === a.id ? "…" : "PDF"}
                            </button>
                          </div>
                        ) : <span className="text-[#878787] text-sm">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function ResultDetail() {
  const { assignmentId } = useParams();
  const navigate = useNavigate();
  return <ReportView assignmentId={assignmentId} onBack={() => navigate(-1)} />;
}

// ---------------- Deliverables ----------------
export function Deliverables() {
  const [items, setItems] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/deliverables").then((r) => setItems(r.data)).catch((e) => setErr(formatApiError(e)));
  }, []);

  const download = async (item) => {
    setDownloading(item.id); setErr("");
    try {
      const res = await api.get(`/deliverables/${item.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = item.filename; document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setDownloading(null);
    }
  };

  if (err) return <Err msg={err} />;
  if (!items) return <Loading />;

  return (
    <div className="space-y-10 fade-in">
      <PageHeader
        eyebrow="CLIENT DELIVERABLES"
        title="Engagement deliverables"
        subtitle="Three editable Word documents plus two walkthrough videos — every artefact you need to send Vodafone Idea. Generated directly from the platform."
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {items.map((d) => {
          const isVideo = d.filename?.endsWith(".mp4");
          return (
            <div key={d.id} className="card p-6 flex flex-col" data-testid={`deliverable-${d.id}`}>
              <div className="flex items-start justify-between mb-4">
                <div className={`w-10 h-10 ${isVideo ? "bg-[#0A0A0A]" : "bg-[#EE2737]"} flex items-center justify-center rounded-sm`}>
                  {isVideo ? <Eye size={18} className="text-white" /> : <FileText size={18} className="text-white" />}
                </div>
                <span className="badge badge-neutral">{isVideo ? `MP4 · ${Math.round((d.size_kb || 0) / 1024)} MB` : `DOCX · ~${d.pages} pp`}</span>
              </div>
              <h3 className="font-heading font-bold text-lg mb-2">{d.name}</h3>
              <p className="text-[13px] text-[#525252] leading-relaxed mb-4 flex-1">{d.description}</p>

              {isVideo && d.available && (
                <video
                  className="w-full mb-4 bg-black"
                  controls
                  preload="metadata"
                  data-testid={`video-${d.id}`}
                  src={`${API}/deliverables/${d.id}/stream`}
                />
              )}

              <div className="overline mb-1">FOR</div>
              <div className="text-[12px] text-[#525252] mb-5">{d.audience}</div>
              <button
                onClick={() => download(d)}
                disabled={!d.available || downloading === d.id}
                className="btn btn-primary !w-full justify-center"
                data-testid={`download-${d.id}`}
              >
                {downloading === d.id ? "Downloading…" : (
                  <><Download size={14} /> Download {isVideo ? ".mp4" : ".docx"}</>
                )}
              </button>
              {!d.available && (
                <div className="text-[11px] text-[#EE2737] mt-2 text-center">Not yet generated</div>
              )}
            </div>
          );
        })}
      </div>

      <div className="card p-6 border-l-4 !border-l-[#EE2737]">
        <div className="overline mb-2">BRANDED & READY</div>
        <h4 className="font-heading font-bold text-base mb-2">Mercury Solutions · Vodafone Idea co-branded</h4>
        <p className="text-[13px] text-[#525252] leading-relaxed max-w-3xl">
          All three Word documents carry the Mercury Solutions logo (cover, top-left) and the Vodafone Idea logo (cover, top-right · &ldquo;Prepared for&rdquo;). The two walkthrough videos are narrated with a natural Indian English AI voice and show every module in a live demo recording.
        </p>
      </div>
    </div>
  );
}

// ---------------- Shared Report View ----------------
export function ReportView({ assignmentId, onBack }) {
  const [r, setR] = useState(null);
  const [err, setErr] = useState("");
  const [scoring, setScoring] = useState(false);
  const [pollCount, setPollCount] = useState(0);
  useEffect(() => {
    let cancelled = false;
    let timer;
    const fetchOnce = () => {
      api.get(`/results/${assignmentId}`)
        .then((res) => { if (!cancelled) { setR(res.data); setScoring(false); } })
        .catch((e) => {
          if (cancelled) return;
          // 202 = scoring in progress → poll every 6s for up to ~3 minutes
          if (e?.response?.status === 202) {
            setScoring(true);
            setPollCount((n) => n + 1);
            if (pollCount < 30) {
              timer = setTimeout(fetchOnce, 6000);
            } else {
              setErr("Scoring is taking unusually long. Please contact Mercury support.");
            }
          } else {
            setErr(formatApiError(e));
          }
        });
    };
    fetchOnce();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignmentId]);

  if (err) return <Err msg={err} />;
  if (scoring && !r) return (
    <div className="card p-10 text-center fade-in" data-testid="report-scoring-progress">
      <div className="inline-block animate-spin rounded-full h-10 w-10 border-2 border-[#EE2737] border-t-transparent mb-4" />
      <h2 className="font-heading font-black text-2xl mb-2">Your AI-scored report is being prepared</h2>
      <p className="text-[14px] text-[#525252] max-w-md mx-auto leading-relaxed">
        Our AI is reading and scoring each of your open responses, then a final integrity verdict is being generated. This usually finishes within 60 seconds.
      </p>
      <p className="text-[11px] text-[#878787] mt-4">Auto-refreshing every 6 seconds · attempt {pollCount}</p>
    </div>
  );
  if (!r) return <Loading />;

  const radarData = r.competencies_meta.map((c) => ({
    competency: c.name.split(" ").slice(0, 2).join(" "),
    current: (r.competency_scores || {})[c.key] || 0,
    target: (r.target_proficiency || {})[c.key] || 3,
  }));

  return (
    <div className="space-y-8 fade-in">
      <div className="flex justify-between items-center flex-wrap gap-3">
        {onBack ? (
          <button onClick={onBack} className="btn btn-ghost !px-2" data-testid="back-button">← Back</button>
        ) : <span />}
        <button
          onClick={async () => {
            trackDownload("report_pdf", { participant: r.participant_name });
            const res = await api.get(`/results/${assignmentId}/export.pdf`, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = url; a.download = `COMPASS_${(r.participant_name || "report").replace(/\s+/g, "_")}.pdf`;
            document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
          }}
          className="btn btn-secondary !py-1.5 !px-3 !text-xs"
          data-testid="download-report-pdf"
        >
          <Download size={12} /> Download PDF
        </button>
      </div>

      <IntegrityBanner integrity={r.integrity} assignmentId={assignmentId} />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        <div className="lg:col-span-2 card p-8">
          <div className="overline mb-2">COMPETENCY ASSESSMENT REPORT</div>
          <div className="flex items-baseline justify-between flex-wrap gap-4">
            <div>
              <h2 className="font-heading font-black text-3xl tracking-tight">{r.participant_name}</h2>
              <div className="text-[#525252] mt-1 text-sm">
                <span className="badge badge-red mr-2">{r.role_level}</span>
                {r.region}
              </div>
            </div>
            <div className="text-right">
              <div className="overline">OVERALL</div>
              <div className="font-heading font-black text-5xl text-[#EE2737] tracking-tighter">{r.overall_score}</div>
              <div className="text-[11px] uppercase tracking-[0.15em] text-[#878787] font-semibold">/ 5.00 BARS</div>
            </div>
          </div>
        </div>
        <div className="card p-6">
          <div className="overline mb-1">EVIDENCE CHANNELS</div>
          <h4 className="font-heading font-bold text-base mb-4">Composite blended from</h4>
          <ul className="text-[13px] space-y-2">
            <li className="flex justify-between font-semibold"><span className="text-[#EE2737]">AI Role-Play (Live)</span><span>35%</span></li>
            <li className="flex justify-between"><span>SJT (objective)</span><span className="font-semibold">20%</span></li>
            <li className="flex justify-between"><span>Manager 360°</span><span className="font-semibold">15%</span></li>
            <li className="flex justify-between"><span>Knowledge MCQ</span><span className="font-semibold">10%</span></li>
            <li className="flex justify-between"><span>AI-scored Open</span><span className="font-semibold">10%</span></li>
            <li className="flex justify-between"><span>Channel Partner 360°</span><span className="font-semibold">5%</span></li>
            <li className="flex justify-between"><span>Self-rating</span><span className="font-semibold">5%</span></li>
          </ul>
        </div>
      </div>

      {/* Radar */}
      <div className="card p-6">
        <div className="overline mb-1">PROFICIENCY RADAR</div>
        <h3 className="font-heading font-bold text-xl mb-4">Current vs target proficiency</h3>
        <div style={{ width: "100%", height: 380 }}>
          <ResponsiveContainer>
            <RadarChart data={radarData}>
              <PolarGrid stroke="#E5E7EB" />
              <PolarAngleAxis dataKey="competency" tick={{ fill: "#525252", fontSize: 11 }} />
              <PolarRadiusAxis angle={90} domain={[0, 5]} tick={{ fill: "#878787", fontSize: 10 }} />
              <Radar name="Current" dataKey="current" stroke="#EE2737" fill="#EE2737" fillOpacity={0.25} strokeWidth={2} />
              <Radar name="Target" dataKey="target" stroke="#0A0A0A" fill="#0A0A0A" fillOpacity={0.05} strokeWidth={1.5} strokeDasharray="4 3" />
              <Legend wrapperStyle={{ fontSize: 12, fontFamily: "Outfit" }} />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Per-competency breakdown */}
      <div className="card p-6">
        <div className="overline mb-1">SCORE BREAKDOWN</div>
        <h3 className="font-heading font-bold text-xl mb-4">Per-competency · per evidence channel</h3>
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Competency</th>
                <th>Composite</th>
                <th>Target</th>
                <th>AI Role-Play</th>
                <th>SJT</th>
                <th>Manager</th>
                <th>MCQ</th>
                <th>Open(AI)</th>
                <th>Channel</th>
                <th>Self</th>
                <th>Δ</th>
              </tr>
            </thead>
            <tbody>
              {r.competencies_meta.map((c) => {
                const s = (r.competency_scores || {})[c.key] || 0;
                const t = (r.target_proficiency || {})[c.key] || 3;
                const b = (r.breakdown || {})[c.key] || {};
                const delta = (s - t).toFixed(1);
                return (
                  <tr key={c.key}>
                    <td className="font-semibold">{c.name}</td>
                    <td><HeatCell value={s} /></td>
                    <td className="text-[#525252]">{t}</td>
                    <td>{b.ai_roleplay ? <span className="font-bold text-[#EE2737]">{b.ai_roleplay}</span> : "—"}</td>
                    <td>{b.sjt || "—"}</td>
                    <td>{b.manager || "—"}</td>
                    <td>{b.mcq || "—"}</td>
                    <td>{b.open || "—"}</td>
                    <td>{b.channel_partner || "—"}</td>
                    <td>{b.self || "—"}</td>
                    <td>
                      <span className={`inline-flex items-center gap-1 font-semibold ${delta >= 0 ? "text-[#008A3D]" : "text-[#EE2737]"}`}>
                        {delta >= 0 ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}{delta}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Strengths + Gaps */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card p-6">
          <div className="overline mb-1 !text-[#008A3D]">STRENGTHS</div>
          <h3 className="font-heading font-bold text-xl mb-4">Above target</h3>
          {(r.strengths || []).length === 0 && <p className="text-[#878787] text-sm">No competencies above target yet.</p>}
          <div className="space-y-3">
            {(r.strengths || []).map((s) => (
              <div key={s.key} className="flex justify-between items-center p-3 bg-[#E6F4EC] border-l-2 border-[#008A3D]">
                <div className="font-semibold text-sm">{s.name}</div>
                <div className="font-heading font-black">{s.score} <span className="text-xs text-[#878787]">/ {s.target}</span></div>
              </div>
            ))}
          </div>
        </div>
        <div className="card p-6">
          <div className="overline mb-1 !text-[#EE2737]">DEVELOPMENT ZONES</div>
          <h3 className="font-heading font-bold text-xl mb-4">Below target — prioritised</h3>
          {(r.gaps || []).length === 0 && <p className="text-[#878787] text-sm">No gaps below target — strong all-round.</p>}
          <div className="space-y-3">
            {(r.gaps || []).map((g) => (
              <div key={g.key} className="flex justify-between items-center p-3 bg-[#FEE2E2] border-l-2 border-[#EE2737]">
                <div>
                  <div className="font-semibold text-sm">{g.name}</div>
                  <div className="text-xs text-[#525252] mt-0.5">Gap: -{g.gap}</div>
                </div>
                <div className="font-heading font-black">{g.score} <span className="text-xs text-[#878787]">/ {g.target}</span></div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* IDP */}
      {(r.idp || []).length > 0 && (
        <div className="card p-6">
          <div className="overline mb-1">PERSONALISED IDP</div>
          <h3 className="font-heading font-bold text-xl mb-6">Top 3 development priorities · next 90 days</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {(r.idp || []).map((p, i) => (
              <div key={i} className="border border-[#E5E7EB] p-5">
                <div className="overline mb-2">PRIORITY {i + 1}</div>
                <h4 className="font-heading font-bold text-base">{p.competency}</h4>
                <div className="text-xs text-[#525252] mt-1 mb-4">
                  Current <span className="font-heading font-black text-[#EE2737]">{p.current}</span> →
                  Target <span className="font-heading font-black text-[#0A0A0A]">{p.target}</span>
                </div>
                <ul className="space-y-2 text-sm">
                  {p.actions.map((a, j) => (
                    <li key={j} className="flex gap-2 text-[#525252]">
                      <span className="text-[#EE2737] font-black">›</span>
                      <span>{a}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* AI Role-Play insights */}
      {r.roleplay_insights && r.roleplay_insights.length > 0 && (
        <div className="card p-6">
          <div className="overline mb-1 !text-[#EE2737]">AI ROLE-PLAY · LIVE CONVERSATION ANALYSIS</div>
          <h3 className="font-heading font-bold text-xl mb-4">Performance in live AI exercises</h3>
          <div className="space-y-6">
            {r.roleplay_insights.map((rp, i) => (
              <div key={i} className="border border-[#E5E7EB] p-5">
                <div className="flex items-start justify-between mb-2 gap-3 flex-wrap">
                  <h4 className="font-heading font-bold text-base">{rp.exercise_name}</h4>
                  {rp.consultant_override && (
                    <span className="badge badge-info" data-testid="override-badge">
                      Reviewed by {rp.consultant_override.reviewer_name}
                    </span>
                  )}
                </div>
                {rp.overall_summary && (
                  <p className="text-[14px] text-[#0A0A0A] italic mb-4 leading-relaxed">"{rp.overall_summary}"</p>
                )}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {Object.entries(rp.scores || {}).map(([k, v]) => {
                    const aiV = rp.ai_scores?.[k];
                    const overridden = rp.consultant_override?.scores?.[k] !== undefined;
                    return (
                      <div key={k} className="bg-[#F8F9FA] p-3 border-l-2 border-[#EE2737]">
                        <div className="overline mb-1">{k.replace(/_/g, " ")}</div>
                        <div className="flex items-baseline justify-between mb-1">
                          <span className="font-heading font-black text-2xl text-[#EE2737]">{v}</span>
                          <span className="text-[10px] text-[#878787] font-semibold">/ 5{overridden && aiV !== undefined && aiV !== v ? ` (AI: ${aiV})` : ""}</span>
                        </div>
                        <p className="text-[11px] text-[#525252] italic leading-snug">{(rp.justifications || {})[k] || "—"}</p>
                      </div>
                    );
                  })}
                </div>
                {rp.session_id && <TranscriptViewer sessionId={rp.session_id} />}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* AI insights */}
      {r.ai_insights && r.ai_insights.length > 0 && (
        <div className="card p-6">
          <div className="overline mb-1">AI ASSESSOR NOTES · OPEN RESPONSES</div>
          <h3 className="font-heading font-bold text-xl mb-4">Reasoning on written open-response answers</h3>
          {r.ai_insights.map((ins, i) => (
            <div key={i} className="border-l-2 border-[#0A0A0A] pl-4 mb-4">
              <div className="text-xs uppercase tracking-wider text-[#878787] font-semibold mb-1">
                {ins.competency.replace(/_/g, " ")} · Score {ins.score}/5
              </div>
              <p className="text-sm text-[#525252] italic">"{ins.justification}"</p>
            </div>
          ))}
        </div>
      )}

      <PerformanceOverlayCard email={r.assignment?.participant_email} />
      <LongitudinalTrendCard email={r.assignment?.participant_email} competenciesMeta={r.competencies_meta} />
    </div>
  );
}

export function TranscriptViewer({ sessionId }) {
  const [open, setOpen] = useState(false);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && !session && !loading) {
      setLoading(true); setErr("");
      try {
        const r = await api.get(`/roleplay/sessions/${sessionId}`);
        setSession(r.data);
      } catch (e) {
        setErr(formatApiError(e));
      } finally { setLoading(false); }
    }
  };

  return (
    <div className="mt-4 border-t border-[#E5E7EB] pt-3" data-testid={`transcript-viewer-${sessionId}`}>
      <button
        onClick={toggle}
        className="flex items-center gap-2 text-[12px] font-semibold uppercase tracking-wider text-[#525252] hover:text-[#EE2737] transition-colors"
        data-testid={`transcript-toggle-${sessionId}`}
      >
        <MessageSquare size={13} />
        {open ? "Hide" : "View"} full conversation transcript
        {open ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
      </button>

      {open && (
        <div className="mt-3 bg-[#FAFAFA] border border-[#E5E7EB] p-4 max-h-[460px] overflow-y-auto space-y-3 fade-in">
          {loading && <div className="text-[12px] text-[#878787]">Loading transcript…</div>}
          {err && <div className="text-[12px] text-[#EE2737]">{err}</div>}
          {session && (
            <>
              <div className="text-[11px] text-[#878787] uppercase tracking-wider font-semibold mb-2">
                {session.messages?.length || 0} messages · Language: {session.language || "en-IN"} · {(session.started_at || "").slice(0, 16).replace("T", " ")}
              </div>
              {(session.messages || []).map((m, i) => (
                <div
                  key={i}
                  className={`px-3 py-2.5 text-[13px] leading-relaxed ${
                    m.role === "user"
                      ? "bg-white border-l-2 border-[#0A0A0A]"
                      : "bg-[#FEF3C7] border-l-2 border-[#D97706]"
                  }`}
                  data-testid={`transcript-msg-${i}`}
                >
                  <div className="text-[9px] uppercase tracking-[0.18em] font-bold mb-1 opacity-70">
                    {m.role === "user" ? "PARTICIPANT" : "AI · " + (session.exercise_name || "").split("—")[0].trim()}
                  </div>
                  <div className="text-[#0A0A0A]">{m.content}</div>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
